import { gql, useQuery } from '@apollo/client';
import { Box, Button, Fade, Stack, Text } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { useWebflowPoc } from '@/hooks/useWebflowPoc';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { MilleFeuille, MilleFeuilleMain } from '@/layouts/MilleFeuille';
import { AlphabetSoupProvider } from '@/modules/alphabet-soup';
import { FooterRender } from '@/modules/website-builder';
import {
  WebsiteBuilderNavigationFormValues,
  WebsiteBuilderPageFormValues,
} from '@/modules/website-builder/types';
import {
  ACCESS_TOKEN_COOKIE_KEY,
  tokenStorage,
} from '@/modules/accounts/tokenStorage';
import { PAGE_FRAGMENT, TENANT_FRAGMENT } from '@/queries';

import { WebsiteBuilder_Navigation } from './graphql-types/WebsiteBuilder_Navigation';
import {
  WebsiteBuilder_Page,
  WebsiteBuilder_PageVariables,
} from './graphql-types/WebsiteBuilder_Page';
import { WebsiteBuilder_Tenant } from './graphql-types/WebsiteBuilder_Tenant';

import { AutoSave } from './components/AutoSave';
import { Header } from './components/Header';
import { IsolatedPageRender } from './components/IsolatedPageRender';
import { Sidebar } from './components/Sidebar';

const TENANT = gql`
  query WebsiteBuilder_Tenant {
    tenant {
      result {
        _id
        ...TenantFragment
      }
    }
  }
  ${TENANT_FRAGMENT}
`;

const NAVIGATION_ITEM = gql`
  fragment WebsiteBuilder_NavigationItem on NavigationItem {
    href
    isExternal
    label
    restriction
    defaultItemIdentifier
    page {
      _id
      name
      restriction
    }
    structure
  }
`;

const NAVIGATION = gql`
  query WebsiteBuilder_Navigation {
    navigation(
      input: { isUnpublishedVersion: true, isRestrictionIgnored: true }
    ) {
      items {
        ...WebsiteBuilder_NavigationItem
        children {
          ...WebsiteBuilder_NavigationItem
          children {
            ...WebsiteBuilder_NavigationItem
          }
        }
      }
      metadata {
        version
        publishedVersion
        isRestrictionIgnored
      }
    }
  }
  ${NAVIGATION_ITEM}
`;

const PAGE = gql`
  query WebsiteBuilder_Page($input: PageInput!) {
    page(input: $input) {
      _id
      ...PageFragment
      ...Sidebar_PageFragment
    }
  }
  ${PAGE_FRAGMENT}
  ${Sidebar.pageFragment}
`;

// Inner component that assumes auth has already been verified
const WebsiteBuilderInner: React.FC = () => {
  const router = useRouter();
  const webflowPoc = useWebflowPoc();
  const isWebflowEnabled = webflowPoc.isEnabled;
  const tenantQuery = useQuery<WebsiteBuilder_Tenant>(TENANT);

  const navigationQuery = useQuery<WebsiteBuilder_Navigation>(NAVIGATION);

  const navigationMethods = useForm<WebsiteBuilderNavigationFormValues>({
    defaultValues: { items: navigationQuery.data?.navigation.items },
  });

  React.useEffect(
    () =>
      navigationMethods.reset({
        items: navigationQuery.data?.navigation.items,
      }),
    [navigationMethods, navigationQuery.data?.navigation.items]
  );

  React.useEffect(() => {
    if (!router.isReady || !isWebflowEnabled) {
      return;
    }

    if (router.query.pageId) {
      router.replace('/website-builder?tabIndex=1');
    }
  }, [isWebflowEnabled, router]);

  const pageQuery = useQuery<WebsiteBuilder_Page, WebsiteBuilder_PageVariables>(
    PAGE,
    {
      variables: {
        input: {
          id: router.query.pageId?.toString(),
          isUnpublishedVersion: true,
        },
      },
      skip: isWebflowEnabled || !router.query.pageId,
    }
  );

  const pageMethods = useForm<WebsiteBuilderPageFormValues>({
    defaultValues: {
      content: undefined,
    },
  });

  React.useEffect(
    () =>
      pageMethods.reset({
        content: pageQuery.data?.page?.content,
      }),
    [pageMethods, pageQuery.data?.page?.content]
  );

  const isLoading =
    tenantQuery.loading ||
    navigationQuery.loading ||
    (!isWebflowEnabled && pageQuery.loading);

  const hasError =
    tenantQuery.error ||
    navigationQuery.error ||
    (!isWebflowEnabled && pageQuery.error);

  return (
    <AlphabetSoupProvider isWebsiteBuilder={true}>
      <Stack spacing="0" h="100vh">
        <Header
          pageMethods={pageMethods}
          navigationMethods={navigationMethods}
        />
        <Stack direction="row" spacing="0" flex="1" overflow="hidden">
          <Sidebar
            key={pageQuery.data?.page._id || 'webflow-navigation'}
            pageMethods={pageMethods}
            navigationMethods={navigationMethods}
            page={pageQuery.data?.page}
          />
          <Box flex="1" p="4" bg="gray.200" zIndex="0">
            {isLoading ? null : hasError || !tenantQuery.data?.tenant.result ? (
              <UnexpectedError />
            ) : isWebflowEnabled ? (
              <Box
                bg="white"
                borderRadius="md"
                borderWidth="1px"
                borderColor="gray.200"
                p="8"
                h="100%"
                overflowY="auto"
              >
                <Stack spacing="3" maxW="xl">
                  <Text fontWeight="semibold" fontSize="lg">
                    Navigation only
                  </Text>
                  <Text color="gray.600">
                    Static marketing pages render fully from Webflow (including
                    Webflow&apos;s own navigation). Use this panel for Tradewing
                    app routes (Community, Events, Learning, …) — link
                    destinations and who can see each tab. Edit static page
                    bodies and Webflow nav in Webflow Designer.
                  </Text>
                  {webflowPoc.designerUrl ? (
                    <Button
                      as="a"
                      href={webflowPoc.designerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      alignSelf="flex-start"
                    >
                      Open Webflow Designer
                    </Button>
                  ) : null}
                </Stack>
              </Box>
            ) : (
              <Box sx={{ zoom: 0.75 }} bg="gray.50" overflowY="auto" h="100%">
                {pageQuery.data && (
                  <AutoSave page={pageQuery.data.page} methods={pageMethods} />
                )}
                <MilleFeuille pointerEvents="none">
                  <MilleFeuilleMain>
                    <Fade in key={router.query.pageId?.toString()}>
                      <IsolatedPageRender methods={pageMethods} />
                    </Fade>
                  </MilleFeuilleMain>
                  <FooterRender
                    tenant={tenantQuery.data.tenant.result}
                    pointerEvents="none"
                  />
                </MilleFeuille>
              </Box>
            )}
          </Box>
        </Stack>
      </Stack>
    </AlphabetSoupProvider>
  );
};

export const WebsiteBuilder: React.FC = () => {
  const router = useRouter();
  const [isCheckingAuth, setIsCheckingAuth] = React.useState(true);
  const [isAuthorized, setIsAuthorized] = React.useState(false);

  const AUTH_REDIRECT_FLAG = 'wbAuthRedirected';

  const getAccessTokenFromCookies = React.useCallback(() => {
    const direct = tokenStorage.getItem(ACCESS_TOKEN_COOKIE_KEY);
    if (direct) return direct;

    if (typeof document === 'undefined') return null;
    const encodedMatch = document.cookie
      ?.split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith('accounts%3AaccessToken='));

    if (encodedMatch) {
      return encodedMatch.split('=')[1] || null;
    }

    return null;
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!router.isReady) {
      setIsCheckingAuth(true);
      return;
    }

    const accessToken = getAccessTokenFromCookies();

    if (!accessToken) {
      const redirectPath = router.asPath || '/website-builder';
      const alreadyRedirected = window.sessionStorage.getItem(AUTH_REDIRECT_FLAG);
      if (!alreadyRedirected) {
        window.sessionStorage.setItem(AUTH_REDIRECT_FLAG, 'true');
        router.replace({
          pathname: '/login',
          query: {
            redirect: redirectPath,
            forceLogin: 'true',
          },
        });
        setIsAuthorized(false);
      } else {
        setIsAuthorized(false);
      }
    } else {
      window.sessionStorage.removeItem(AUTH_REDIRECT_FLAG);
      setIsAuthorized(true);
    }

    setIsCheckingAuth(false);
  }, [getAccessTokenFromCookies, router, router.isReady]);

  if (isCheckingAuth) {
    return <Loading />;
  }

  if (!isAuthorized) {
    const redirectPath = router.asPath || '/website-builder';
    const loginHref = {
      pathname: '/login',
      query: { redirect: redirectPath },
    };

    return (
      <Stack spacing="3" align="center" justify="center" h="100vh">
        <Loading />
        <Text color="gray.600" textAlign="center" px="6">
          Authentication is required to use the Website Builder. Please sign in to
          continue.
        </Text>
        <Button
          colorScheme="purple"
          onClick={() => router.replace(loginHref)}
          size="sm"
        >
          Go to Login
        </Button>
      </Stack>
    );
  }

  return <WebsiteBuilderInner />;
};
